import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { faqJsonLd, AUDIT_FAQ } from "@/lib/faqSchemas";
import { Eyebrow, SectionLabel } from "@/components/Eyebrow";
import { api } from "@/lib/api";
import { toast } from "sonner";

const WHAT_YOU_GET = [
  "Transcript review against the Common Prerequisites Manual for your intended major",
  "Target-school analysis (UF, UCF, FSU, USF, FIU, or nationwide)",
  "Identification of completed prerequisites, missing prerequisites, and excess credits",
  "Risk assessment with categorical readiness signals",
  "Written semester-by-semester roadmap for the rest of your AA",
  "A 60-minute strategy call to walk through the report",
];

const BASE_PRICE = 249;
const REFERRAL_DISCOUNT = 50;

export default function TransferAudit() {
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [codeStatus, setCodeStatus] = useState(null); // null | {valid, message, discount_cents}
  const [checking, setChecking] = useState(false);

  // Auto-fill from ?ref=BV-XXXXXX
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (ref) {
      setCode(ref.toUpperCase());
      // Auto-validate
      validate(ref.toUpperCase());
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const validate = async (raw) => {
    const c = (raw ?? code).trim().toUpperCase();
    if (!c) { setCodeStatus(null); return; }
    setChecking(true);
    try {
      const res = await api.post("/checkout/validate-referral", { code: c });
      setCodeStatus(res.data);
    } catch { setCodeStatus({ valid: false, message: "Could not validate code." }); }
    finally { setChecking(false); }
  };

  const finalPrice = codeStatus?.valid ? BASE_PRICE - REFERRAL_DISCOUNT : BASE_PRICE;

  const buy = async () => {
    setBusy(true);
    try {
      const payload = { tier_id: "transfer_audit", origin_url: window.location.origin };
      if (codeStatus?.valid) payload.referral_code = code.trim().toUpperCase();
      const res = await api.post("/checkout/session", payload);
      if (res.data.url) window.location.href = res.data.url;
      else toast.error("Stripe did not return a checkout URL");
    } catch { toast.error("Failed to start checkout"); }
    finally { setBusy(false); }
  };
  return (
    <Layout>
      <SEO
        title="The Transfer Audit — Comprehensive $249 Transfer Review"
        description="A one-time $249 comprehensive audit: transcript review, target-school analysis, prerequisite gaps, risk assessment, semester-by-semester roadmap, and a 60-minute strategy call."
        canonical="/transfer-audit"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Transfer Audit",
          description: "One-time comprehensive review of a Florida transfer candidate's transcript, prerequisites, and roadmap.",
          brand: { "@type": "Brand", name: "Beyond Valencia" },
          offers: {
            "@type": "Offer",
            price: 249,
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            url: "https://ivyleagueconsulting.org/transfer-audit",
          },
        }, faqJsonLd(AUDIT_FAQ)]}
      />
      <section className="border-b border-stone/30 bg-bone" data-testid="transfer-audit-page">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-20 lg:pt-28 pb-16">
          <Eyebrow>Flagship product</Eyebrow>
          <h1 className="font-serif font-medium text-4xl sm:text-5xl lg:text-[5rem] text-ink leading-[1.02] tracking-tightest mt-5">The Transfer Audit.</h1>
          <p className="mt-7 text-ink-soft text-base sm:text-lg max-w-2xl leading-relaxed">
            A one-time, comprehensive review of where you stand and what comes next.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-20 lg:py-24 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-7">
          <SectionLabel index="01" label="What you get" />
          <ul className="mt-6 space-y-3 border-t border-stone/30 pt-5">
            {WHAT_YOU_GET.map((b) => (
              <li key={b} className="flex items-start gap-3 text-ink"><span className="text-brass mt-2 h-px w-3 bg-brass flex-shrink-0" /><span className="text-sm leading-relaxed">{b}</span></li>
            ))}
          </ul>

          <div className="mt-12">
            <SectionLabel index="02" label="Who it's for" />
            <p className="mt-4 text-ink-soft leading-relaxed">
              First-semester Valencia students through students with one semester left. Florida residents and out-of-state students considering Florida pathways.
            </p>
          </div>

          <div className="mt-10">
            <SectionLabel index="03" label="What it isn't" />
            <p className="mt-4 text-ink-soft leading-relaxed">Application help. Essay editing. A guarantee.</p>
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="border border-stone/40 bg-bone-dim p-8" data-testid="audit-pricing-card">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brass">What it costs</div>
            {codeStatus?.valid ? (
              <div className="mt-3 flex items-baseline gap-3">
                <p className="font-serif font-medium text-5xl lg:text-6xl text-ivy tracking-tight tabular-nums" data-testid="audit-price-discounted">${finalPrice}.</p>
                <p className="font-serif text-2xl text-stone line-through tabular-nums" data-testid="audit-price-original">${BASE_PRICE}</p>
              </div>
            ) : (
              <p className="mt-3 font-serif font-medium text-5xl lg:text-6xl text-ink tracking-tight tabular-nums" data-testid="audit-price-base">${BASE_PRICE}.</p>
            )}
            <p className="mt-1 text-ink-soft text-sm">One-time. Not a subscription. Not a sales call. A real deliverable.</p>

            {/* Referral code input */}
            <div className="mt-7 border-t border-stone/30 pt-5">
              <label className="text-[11px] uppercase tracking-[0.22em] text-stone" htmlFor="ref-code">Have a referral code?</label>
              <div className="mt-2 flex items-stretch border border-stone/40 bg-bone">
                <input
                  id="ref-code"
                  type="text"
                  value={code}
                  onChange={(e) => { setCode(e.target.value.toUpperCase()); setCodeStatus(null); }}
                  onBlur={() => validate()}
                  placeholder="BV-XXXXXX"
                  className="flex-1 bg-transparent font-mono text-sm text-ink px-3 py-2 tracking-wider focus:outline-none placeholder:text-stone/60"
                  data-testid="audit-referral-input"
                />
                <button
                  type="button"
                  onClick={() => validate()}
                  disabled={checking || !code.trim()}
                  className="border-l border-stone/40 px-4 text-[11px] uppercase tracking-[0.18em] text-ivy hover:bg-ivy hover:text-bone transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="audit-apply-referral-btn"
                >
                  {checking ? "…" : "Apply"}
                </button>
              </div>
              {codeStatus && (
                <p className={`mt-2 text-xs ${codeStatus.valid ? "text-ivy" : "text-brass"}`} data-testid="audit-referral-message">
                  {codeStatus.message}
                </p>
              )}
            </div>

            <button onClick={buy} disabled={busy} className="mt-7 w-full bg-ivy hover:bg-ivy-light text-bone uppercase tracking-[0.18em] text-xs font-semibold py-4 px-6 transition-colors disabled:opacity-60" data-testid="transfer-audit-buy-btn">
              {busy ? "Redirecting…" : `Order Your Transfer Audit — $${finalPrice}`}
            </button>
            <p className="mt-4 text-[11px] uppercase tracking-[0.18em] text-stone text-center">Secure checkout via Stripe</p>
          </div>
        </aside>
      </section>
    </Layout>
  );
}
